import { useState, useEffect, useCallback, useRef } from 'react';
import type { Plan, PlaylistItem, DeviceState } from '@/types';
import { buildPlaylist } from '@/playback/scheduler';
import { getAllCachedPaths } from '@/playback/media-cache';
import { startSync, stopSync, setSyncCallback } from '@/core/sync-manager';
import { startSensor, stopSensor } from '@/sensor/sensor-bridge';
import { startTracking, stopTracking } from '@/sensor/presence-tracker';
import { getCachedPlan } from '@/core/offline-store';
import { logger } from '@/core/logger';
import { platform } from '@/platform';
import PlaybackEngine from '@/playback/PlaybackEngine';

interface PlayerScreenProps {
  deviceState: DeviceState;
  onDiagnostic: () => void;
}

export default function PlayerScreen({ deviceState, onDiagnostic }: PlayerScreenProps) {
  const [playlist, setPlaylist] = useState<PlaylistItem[]>([]);
  // initialIndex feeds into PlaybackEngine to preserve "where were we"
  // continuity across plan rebuilds — see survivedIndex below.
  const [initialIndex, setInitialIndex] = useState(0);
  const [tapCount, setTapCount] = useState(0);

  // Tracks the currently-playing item id so a plan rebuild can continue
  // playback from the same campaign+media instead of jumping back to slot 0.
  const currentMediaIdRef = useRef<string | null>(null);

  const boardId = deviceState.boardId!;

  const rebuildPlaylist = useCallback((plan: Plan) => {
    const mediaPaths = getAllCachedPaths();
    const newPlaylist = buildPlaylist(plan, mediaPaths);

    // Continuity: if the item we're currently showing still exists in the
    // new playlist, jump the engine to its first occurrence so the viewer
    // doesn't see a hard reset back to slot 0 every poll cycle.
    const survivedIndex = currentMediaIdRef.current
      ? newPlaylist.findIndex((p) => p.media.id === currentMediaIdRef.current)
      : -1;

    setPlaylist(newPlaylist);
    setInitialIndex(survivedIndex >= 0 ? survivedIndex : 0);
    logger.info('[Player] Playlist rebuilt', {
      items: newPlaylist.length,
      resumeIndex: survivedIndex >= 0 ? survivedIndex : 0,
    });
  }, []);

  useEffect(() => {
    logger.start(boardId);
    logger.info('PlayerScreen mounted', { boardId });

    // Load cached plan for instant start while the network sync runs.
    const cachedPlan = getCachedPlan();
    if (cachedPlan) {
      rebuildPlaylist(cachedPlan);
    }

    // Set sync callback to rebuild playlist on new data
    setSyncCallback(rebuildPlaylist);

    // Start sync manager
    startSync(boardId);

    // Start sensor
    startSensor();
    startTracking(boardId);

    // Keep the screen awake. On Tizen this calls tizen.power.request;
    // on web it best-effort uses navigator.wakeLock. Lock survives for
    // the lifetime of this effect — released in cleanup.
    let wakeLock: { release: () => void } | null = null;
    if (platform.power.available) {
      void platform.power.keepScreenOn().then((lock) => {
        wakeLock = lock;
        logger.info('Screen wake lock acquired', { platform: platform.kind });
      });
    }

    return () => {
      stopSync();
      stopSensor();
      stopTracking();
      wakeLock?.release();
      logger.info('PlayerScreen unmounted', { boardId });
    };
  }, [boardId, rebuildPlaylist]);

  // Triple-tap top-right corner → diagnostic screen
  const handleCornerTap = () => {
    const newCount = tapCount + 1;
    setTapCount(newCount);
    if (newCount >= 3) {
      setTapCount(0);
      onDiagnostic();
    }
    // Reset after 2s
    setTimeout(() => setTapCount(0), 2000);
  };

  return (
    <>
      <PlaybackEngine
        playlist={playlist}
        boardId={boardId}
        boardCode={deviceState.boardCode}
        initialIndex={initialIndex}
        onCurrentChange={(mediaId) => {
          currentMediaIdRef.current = mediaId;
        }}
      />

      {/* Hidden tap zone for diagnostic access */}
      <div
        onClick={handleCornerTap}
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          width: 80,
          height: 80,
          zIndex: 1000,
        }}
      />
    </>
  );
}
